import React, { useRef, useState } from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import Img from "gatsby-image"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowDown,
  FiArrowUpRight,
  FiGitBranch,
  FiActivity,
  FiAlertCircle,
  FiServer,
  FiLayers,
  FiCpu,
  FiCode,
  FiKey,
  FiFileText,
  FiCheck,
  FiMaximize2,
  FiX,
  FiPlus,
} from "react-icons/fi"
import PageMetadata from "../../components/PageMetadata"
import Link from "../../components/Link"
import { translateMessageId } from "../../utils/translations"

const APP_URL = "https://api-monitor.newinntech.com/auth/login"
const DOCS_URL = "https://api-monitor.newinntech.com/docs"
const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
    font-weight: 500;
  }
  h2 {
    font-size: clamp(2rem, 3.8vw, 3.2rem);
    line-height: 1.12;
    letter-spacing: -0.04em;
    margin: 0 0 1.5rem;
    text-wrap: balance;
  }
  h3 {
    letter-spacing: -0.025em;
  }
  section[id] {
    scroll-margin-top: 6rem;
  }
  a:focus-visible,
  button:focus-visible,
  summary:focus-visible,
  [tabindex="0"]:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
  a:hover svg {
    fill: none;
  }
  svg {
    flex-shrink: 0;
  }
`
const Container = styled.div`
  width: min(100%, 1240px);
  margin: 0 auto;
  padding: 0 2.5rem;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Kicker = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  line-height: 1.7;
  margin-bottom: 1.5rem;
`
const Copy = styled.p`
  color: ${({ theme }) => theme.colors.text200};
  font-size: 1rem;
  line-height: 1.8;
  margin: 0 0 1.5rem;
`
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.25rem 1.75rem;
`
const Primary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #a2dfff;
  color: #0a263a !important;
  border: 1px solid #a2dfff;
  font-size: 0.9rem;
  line-height: 1.5;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background: #d4efff;
    border-color: #d4efff;
  }
`
const Secondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  color: #fff !important;
  padding: 0.7rem 0;
  border-bottom: 1px solid #708f9d;
  font-size: 0.9rem;
  text-decoration: none;
  &:hover {
    border-color: #a2dfff;
  }
`
const TextLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`
const Hero = styled.header`
  position: relative;
  overflow: hidden;
  background: #081721;
  color: #fff;
  background-image: radial-gradient(
    ellipse at 90% 40%,
    #16457380,
    transparent 60%
  );
  a:focus-visible {
    outline-color: #a2dfff;
  }
`
const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  color: #a8bfcd;
  font-size: 0.75rem;
  padding-top: 1.75rem;
  a {
    color: #a8bfcd;
    text-decoration: none;
  }
  a:hover {
    color: #fff;
    text-decoration: underline;
  }
`
const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 3rem;
  align-items: center;
  padding: 4rem 0 5rem;
  ${Kicker} {
    color: #a2dfff;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  ${Kicker} svg {
    width: 1.2rem;
    height: 1.2rem;
  }
  h1 {
    color: #fff;
    font-size: clamp(3rem, 4.9vw, 4.25rem);
    line-height: 1.04;
    letter-spacing: -0.05em;
    margin: 0 0 1.75rem;
  }
  h1 span {
    color: #a2dfff;
    display: block;
  }
  p {
    font-size: 1.05rem;
    line-height: 1.8;
    color: #c3d2dd;
    margin: 0 0 2rem;
    max-width: 480px;
  }
  small {
    display: block;
    color: #9db6c5;
    font-size: 0.75rem;
    line-height: 1.7;
    margin-top: 2rem;
  }
  @media (max-width: 950px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    padding: 3rem 0;
    h1 {
      max-width: 700px;
      font-size: clamp(2.65rem, 7vw, 4.25rem);
    }
    p {
      max-width: 620px;
    }
  }
`
const HeroFrame = styled.figure`
  min-width: 0;
  margin: 0;
  border: 1px solid #35535e;
  background: #0a141b;
  box-shadow: 0 25px 60px #00000040;
  > div:first-child {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 1.1rem 1.25rem;
    border-bottom: 1px solid #2c434e;
  }
  strong {
    color: #e4efef;
    font-size: 0.85rem;
    font-weight: 500;
  }
  > div:first-child span {
    color: #acc5cb;
    font-size: 0.65rem;
  }
  figcaption {
    padding: 1.5rem;
    color: #dce9ed;
    border-top: 1px solid #2c434e;
    font-size: 0.95rem;
    line-height: 1.6;
  }
  ul {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1.5rem;
    padding: 0;
    margin: 1rem 0 0;
    list-style: none;
  }
  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #9dc5df;
    font-size: 0.7rem;
    margin: 0;
  }
  li::before {
    display: none;
  }
  @media (max-width: 500px) {
    figcaption {
      padding: 1rem;
    }
  }
`
const Signals = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2.5rem;
  padding: 2rem 0 2.5rem;
  border-top: 1px solid #355062;
  article {
    min-width: 0;
  }
  span {
    color: #87abc3;
    font-size: 0.65rem;
    letter-spacing: 0.08em;
  }
  h2 {
    color: #fff;
    font-size: 1.15rem;
    letter-spacing: -0.01em;
    margin: 0.6rem 0;
  }
  p {
    color: #a8c0d1;
    font-size: 0.8rem;
    line-height: 1.7;
    margin: 0;
  }
  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    article {
      display: grid;
      grid-template-columns: 2rem 1fr;
      column-gap: 0.75rem;
    }
    span {
      grid-row: span 2;
      padding-top: 0.15rem;
    }
    h2 {
      margin-top: 0;
    }
  }
`
const SectionNav = styled.nav`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  a {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    padding: 1.5rem;
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.85rem;
    text-decoration: none;
  }
  a:first-child {
    border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  a:hover {
    background: ${({ theme }) => theme.colors.background};
  }
  @media (max-width: 600px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
    a,
    a:first-child {
      border: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
      padding: 1rem 0;
    }
    a:last-child {
      border-bottom: 0;
    }
  }
`
const Section = styled.section`
  padding: 5.5rem 0;
  @media (max-width: 650px) {
    padding: 3.5rem 0;
  }
`
const Heading = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 4rem;
  align-items: end;
  margin-bottom: 2.5rem;
  h2,
  p {
    margin-bottom: 0;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`
const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    padding: 1rem 0.75rem;
    font: inherit;
    font-size: 0.85rem;
    line-height: 1.5;
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.lightBorder};
    cursor: pointer;
  }
  button:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  button[aria-selected="true"] {
    background: #102d46;
    color: #bce9ff;
    border-color: #365b78;
  }
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    button:last-child {
      grid-column: 1 / -1;
    }
  }
  @media (max-width: 400px) {
    button {
      font-size: 0.75rem;
      gap: 0.5rem;
      padding: 0.75rem 0.5rem;
    }
  }
`
const ViewPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  &[hidden] {
    display: none;
  }
  &:focus-visible {
    outline-offset: 6px;
  }
`
const ViewIntro = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.72fr;
  gap: 3rem;
  padding: 2rem;
  align-items: center;
  h3 {
    font-size: 1.6rem;
    line-height: 1.2;
    margin: 0 0 1rem;
  }
  p {
    font-size: 0.95rem;
    margin: 0;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 1.25rem;
  }
  li {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    font-size: 0.85rem;
    line-height: 1.6;
    margin: 0 0 0.6rem;
    color: ${({ theme }) => theme.colors.text200};
  }
  li::before {
    display: none;
  }
  li svg {
    align-self: flex-start;
    margin-top: 0.3rem;
    color: ${({ theme }) => theme.colors.primary};
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
  @media (max-width: 500px) {
    padding: 1.5rem;
    h3 {
      font-size: 1.35rem;
    }
  }
`
const Enlarge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  padding: 0.5rem 0;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`
const Screenshot = styled.figure`
  margin: 0;
  padding: 1rem;
  background: #0b141a;
  border-top: 1px solid #293c45;
  > .gatsby-image-wrapper {
    border: 1px solid #2c353b;
  }
  @media (max-width: 500px) {
    padding: 0.5rem;
  }
`
const SourceNote = styled.p`
  font-size: 0.75rem;
  line-height: 1.6;
  margin: 1rem 0 0;
  color: ${({ theme }) => theme.colors.text200};
  a {
    color: inherit;
  }
`
const Lightbox = styled.dialog`
  width: min(96vw, 1700px);
  max-width: none;
  max-height: 92vh;
  padding: 0;
  margin: auto;
  border: 1px solid #415860;
  background: #0b1923;
  color: #fff;
  overflow: hidden;
  &::backdrop {
    background: #020910df;
  }
  button:focus-visible,
  [tabindex]:focus-visible {
    outline-color: #a2dfff;
    outline-offset: -4px;
  }
`
const LightboxBar = styled.div`
  padding: 1rem 1.25rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #3a4c56;
  h2 {
    color: #fff;
    font-size: 1rem;
    margin: 0;
    letter-spacing: 0;
  }
  p {
    color: #b3c5ce;
    font-size: 0.75rem;
    margin: 0.4rem 0 0;
    line-height: 1.5;
  }
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    background: #142c39;
    color: #fff;
    border: 1px solid #48616d;
    cursor: pointer;
  }
`
const LightboxImage = styled.div`
  overflow: auto;
  max-height: calc(92vh - 115px);
  img {
    display: block;
    margin: 0;
    width: 100%;
    min-width: 1000px;
    max-width: none;
    height: auto;
  }
`
const Capabilities = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2.5rem;
  article {
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
    padding-top: 1.75rem;
  }
  article > svg {
    width: 1.6rem;
    height: 1.6rem;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 1.2rem;
  }
  h3 {
    font-size: 1.3rem;
    line-height: 1.3;
    margin: 0 0 1rem;
  }
  p {
    font-size: 0.95rem;
    margin: 0;
  }
  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`
const APIAction = styled.div`
  margin-top: 2.5rem;
`
const WorkflowGrid = styled.div`
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 4rem;
  align-items: stretch;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`
const WorkflowVisual = styled.div`
  position: relative;
  min-width: 0;
  min-height: 540px;
  > .gatsby-image-wrapper {
    position: absolute !important;
    inset: 0;
    height: 100%;
  }
  @media (max-width: 900px) {
    min-height: 350px;
  }
  @media (max-width: 500px) {
    min-height: 290px;
  }
`
const WorkflowList = styled.div`
  margin-top: 2rem;
  article {
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
    padding: 1.5rem 0;
  }
  article:last-child {
    padding-bottom: 0;
  }
  h3 {
    font-size: 1.2rem;
    line-height: 1.3;
    margin: 0 0 0.5rem;
  }
  p {
    font-size: 0.95rem;
    margin: 0;
  }
`
const Start = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2.5rem;
  padding: 0;
  margin: 3rem 0 4rem;
  list-style: none;
  li {
    margin: 0;
    padding-top: 1.5rem;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  li::before {
    display: none;
  }
  span {
    color: ${({ theme }) => theme.colors.primary};
    display: block;
    font-size: 0.75rem;
    margin-bottom: 1.25rem;
  }
  h3 {
    font-size: 1.3rem;
    margin: 0 0 1rem;
    line-height: 1.3;
  }
  p {
    font-size: 0.95rem;
    margin: 0;
  }
  @media (max-width: 750px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`
const FAQ = styled.div`
  display: grid;
  grid-template-columns: 0.7fr 1.3fr;
  gap: 4rem;
  padding-top: 3rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h3 {
    font-size: 1.7rem;
    margin: 0 0 1.5rem;
  }
  details {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    cursor: pointer;
    list-style: none;
    padding: 1.25rem 0;
    font-size: 1rem;
    font-weight: 500;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  details[open] summary svg {
    transform: rotate(45deg);
  }
  details p {
    padding: 0 2rem 1.5rem 0;
    margin: 0;
    font-size: 0.95rem;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`
const CTA = styled(Section)`
  background: #0a2138;
  color: #fff;
  border-top: 3px solid #a2dfff;
  ${Container} {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 4rem;
    align-items: center;
  }
  ${Kicker} {
    color: #a2dfff;
  }
  h2 {
    color: #fff;
    margin-bottom: 0;
  }
  p {
    color: #c4d7df;
  }
  small {
    display: block;
    color: #a4beca;
    font-size: 0.75rem;
    line-height: 1.7;
    margin-top: 1.5rem;
  }
  a:focus-visible {
    outline-color: #a2dfff;
  }
  @media (max-width: 850px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }
`

const views = [
  { id: "overview", icon: FiActivity },
  { id: "traces", icon: FiGitBranch },
  { id: "logs", icon: FiFileText },
  { id: "topology", icon: FiLayers },
  { id: "infra", icon: FiServer },
]
const features = [
  { id: "instrumentation", icon: FiCode },
  { id: "correlation", icon: FiActivity },
  { id: "alerts", icon: FiAlertCircle },
  { id: "ai", icon: FiCpu },
  { id: "workspaces", icon: FiLayers },
  { id: "api", icon: FiKey },
]
const ApiMonitorPage = ({ data }) => {
  const intl = useIntl()
  const t = (id) => translateMessageId(`api-product-${id}`, intl)
  const [activeView, setActiveView] = useState("overview")
  const tabRefs = useRef([])
  const dialogRef = useRef(null)
  const triggerRef = useRef(null)
  const onTabKey = (event, index) => {
    let next
    if (event.key === "ArrowRight") next = (index + 1) % views.length
    else if (event.key === "ArrowLeft")
      next = (index + views.length - 1) % views.length
    else if (event.key === "Home") next = 0
    else if (event.key === "End") next = views.length - 1
    else return
    event.preventDefault()
    setActiveView(views[next].id)
    tabRefs.current[next].focus()
  }
  const openImage = (event) => {
    triggerRef.current = event.currentTarget
    dialogRef.current.showModal()
  }
  const closeOnBackdrop = (event) => {
    if (event.target !== event.currentTarget) return
    const rect = event.currentTarget.getBoundingClientRect()
    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    )
      event.currentTarget.close()
  }
  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
        image={data.overview.childImageSharp.fixed.src}
      />
      <Hero>
        <Container>
          <Breadcrumb aria-label={t("breadcrumb")}>
            <Link to="/productos/" isPartiallyActive={false}>
              {t("breadcrumb")}
            </Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">API Monitor</span>
          </Breadcrumb>
          <HeroGrid>
            <div>
              <Kicker>
                <FiActivity aria-hidden="true" />
                {t("eyebrow")}
              </Kicker>
              <h1>
                {t("hero-title")} <span>{t("hero-accent")}</span>
              </h1>
              <p>{t("hero-intro")}</p>
              <Actions>
                <Primary to="#demo">
                  {t("cta-demo")}
                  <FiArrowRight aria-hidden="true" />
                </Primary>
                <Secondary to="#producto">
                  {t("hero-explore")}
                  <FiArrowDown aria-hidden="true" />
                </Secondary>
              </Actions>
              <small>{t("hero-note")}</small>
            </div>
            <HeroFrame>
              <div>
                <strong>API Monitor</strong>
                <span>{t("frame-label")}</span>
              </div>
              <Img
                fluid={data.overview.childImageSharp.fluid}
                alt={t("view-overview-alt")}
                loading="eager"
                fadeIn={false}
              />
              <figcaption>
                {t("frame-caption")}
                <ul>
                  {["metrics", "traces", "logs"].map((id) => (
                    <li key={id}>
                      <FiCheck aria-hidden="true" />
                      {t(`hero-tag-${id}`)}
                    </li>
                  ))}
                </ul>
              </figcaption>
            </HeroFrame>
          </HeroGrid>
          <Signals>
            {["metrics", "traces", "logs"].map((id, index) => (
              <article key={id}>
                <span aria-hidden="true">0{index + 1}</span>
                <h2>{t(`signal-${id}-title`)}</h2>
                <p>{t(`signal-${id}-description`)}</p>
              </article>
            ))}
          </Signals>
        </Container>
      </Hero>
      <SectionNav aria-label={t("nav-label")}>
        <Container>
          {[
            { id: "gallery", to: "#producto" },
            { id: "capabilities", to: "#capacidades" },
            { id: "start", to: "#empezar" },
          ].map(({ id, to }) => (
            <Link key={id} to={to}>
              {t(`nav-${id}`)}
              <FiArrowDown aria-hidden="true" />
            </Link>
          ))}
        </Container>
      </SectionNav>
      <Section id="producto" aria-labelledby="api-gallery-title">
        <Container>
          <Heading>
            <div>
              <Kicker>{t("gallery-eyebrow")}</Kicker>
              <h2 id="api-gallery-title">{t("gallery-title")}</h2>
            </div>
            <Copy>{t("gallery-intro")}</Copy>
          </Heading>
          <Tabs role="tablist" aria-label={t("gallery-label")}>
            {views.map(({ id, icon: Icon }, index) => (
              <button
                key={id}
                type="button"
                id={`api-tab-${id}`}
                role="tab"
                aria-selected={activeView === id}
                aria-controls={`api-panel-${id}`}
                tabIndex={activeView === id ? 0 : -1}
                ref={(node) => {
                  tabRefs.current[index] = node
                }}
                onClick={() => setActiveView(id)}
                onKeyDown={(event) => onTabKey(event, index)}
              >
                <Icon aria-hidden="true" />
                {t(`view-${id}-label`)}
              </button>
            ))}
          </Tabs>
          {views.map(({ id }) => (
            <ViewPanel
              key={id}
              id={`api-panel-${id}`}
              role="tabpanel"
              aria-labelledby={`api-tab-${id}`}
              tabIndex={0}
              hidden={activeView !== id}
            >
              <ViewIntro>
                <div>
                  <h3>{t(`view-${id}-title`)}</h3>
                  <Copy>{t(`view-${id}-description`)}</Copy>
                </div>
                <div>
                  <ul>
                    {[1, 2].map((n) => (
                      <li key={n}>
                        <FiCheck aria-hidden="true" />
                        {t(`view-${id}-point-${n}`)}
                      </li>
                    ))}
                  </ul>
                  <Enlarge type="button" onClick={openImage}>
                    <FiMaximize2 aria-hidden="true" />
                    {t("gallery-enlarge")}
                  </Enlarge>
                </div>
              </ViewIntro>
              <Screenshot>
                <Img
                  fluid={data[id].childImageSharp.fluid}
                  alt={t(`view-${id}-alt`)}
                  fadeIn={false}
                />
              </Screenshot>
            </ViewPanel>
          ))}
          <SourceNote>
            <Link to={DOCS_URL} hideArrow>
              {t("gallery-source")}
            </Link>
          </SourceNote>
        </Container>
      </Section>
      <Lightbox
        ref={dialogRef}
        aria-labelledby="api-lightbox-title"
        onClick={closeOnBackdrop}
        onClose={() => triggerRef.current?.focus()}
      >
        <LightboxBar>
          <div>
            <h2 id="api-lightbox-title">{t(`view-${activeView}-label`)}</h2>
            <p>{t("gallery-pan")}</p>
          </div>
          <button
            type="button"
            aria-label={t("gallery-close")}
            onClick={() => dialogRef.current.close()}
          >
            <FiX size={22} aria-hidden="true" />
          </button>
        </LightboxBar>
        <LightboxImage tabIndex={0} role="region" aria-label={t("gallery-pan")}>
          <img
            src={data[activeView].childImageSharp.fluid.src}
            alt={t(`view-${activeView}-alt`)}
          />
        </LightboxImage>
      </Lightbox>
      <Capabilities id="capacidades" aria-labelledby="api-capabilities-title">
        <Container>
          <Heading>
            <div>
              <Kicker>{t("capabilities-eyebrow")}</Kicker>
              <h2 id="api-capabilities-title">{t("capabilities-title")}</h2>
            </div>
            <Copy>{t("capabilities-intro")}</Copy>
          </Heading>
          <FeatureGrid>
            {features.map(({ id, icon: Icon }) => (
              <article key={id}>
                <Icon aria-hidden="true" />
                <h3>{t(`feature-${id}-title`)}</h3>
                <Copy>{t(`feature-${id}-description`)}</Copy>
              </article>
            ))}
          </FeatureGrid>
          <APIAction>
            <TextLink to={`${DOCS_URL}/api-reference/ingesta-manual`} hideArrow>
              {t("api-link")}
              <FiArrowUpRight aria-hidden="true" />
            </TextLink>
          </APIAction>
        </Container>
      </Capabilities>
      <Section aria-labelledby="api-workflow-title">
        <Container>
          <WorkflowGrid>
            <WorkflowVisual>
              <Img
                fluid={data.editorial.childImageSharp.fluid}
                alt={t("workflow-photo-alt")}
                imgStyle={{ objectPosition: "58% center" }}
              />
            </WorkflowVisual>
            <div>
              <Kicker>{t("workflow-eyebrow")}</Kicker>
              <h2 id="api-workflow-title">{t("workflow-title")}</h2>
              <WorkflowList>
                {["detect", "trace", "investigate"].map((id) => (
                  <article key={id}>
                    <h3>{t(`workflow-${id}-title`)}</h3>
                    <Copy>{t(`workflow-${id}-description`)}</Copy>
                  </article>
                ))}
              </WorkflowList>
            </div>
          </WorkflowGrid>
        </Container>
      </Section>
      <Start id="empezar" aria-labelledby="api-start-title">
        <Container>
          <Heading>
            <div>
              <Kicker>{t("start-eyebrow")}</Kicker>
              <h2 id="api-start-title">{t("start-title")}</h2>
            </div>
            <Copy>{t("start-intro")}</Copy>
          </Heading>
          <Steps>
            {["scope", "instrument", "validate"].map((id, index) => (
              <li key={id}>
                <span aria-hidden="true">0{index + 1}</span>
                <h3>{t(`step-${id}-title`)}</h3>
                <Copy>{t(`step-${id}-description`)}</Copy>
              </li>
            ))}
          </Steps>
          <FAQ>
            <div>
              <h3>{t("faq-title")}</h3>
              <TextLink to={DOCS_URL} hideArrow>
                {t("docs-link")}
                <FiArrowUpRight aria-hidden="true" />
              </TextLink>
            </div>
            <div>
              {["apm", "connect", "access"].map((id) => (
                <details key={id}>
                  <summary>
                    {t(`faq-${id}-question`)}
                    <FiPlus aria-hidden="true" />
                  </summary>
                  <Copy>{t(`faq-${id}-answer`)}</Copy>
                </details>
              ))}
            </div>
          </FAQ>
        </Container>
      </Start>
      <CTA id="demo" aria-labelledby="api-cta-title">
        <Container>
          <div>
            <Kicker>{t("cta-eyebrow")}</Kicker>
            <h2 id="api-cta-title">{t("cta-title")}</h2>
          </div>
          <div>
            <Copy>{t("cta-description")}</Copy>
            <Actions>
              <Primary
                to={`mailto:gerencia@newinntech.com?subject=${encodeURIComponent(
                  t("cta-subject")
                )}`}
                hideArrow
              >
                {t("cta-demo")}
                <FiArrowRight aria-hidden="true" />
              </Primary>
              <Secondary to={APP_URL} hideArrow>
                {t("app-link")}
                <FiArrowUpRight aria-hidden="true" />
              </Secondary>
            </Actions>
            <small>{t("cta-note")}</small>
          </div>
        </Container>
      </CTA>
    </Page>
  )
}
export default ApiMonitorPage
export const query = graphql`
  query ApiMonitorProductPageQuery {
    overview: file(relativePath: { eq: "api-monitor/overview-official.png" }) {
      ...ApiMonitorScreenshot
      childImageSharp {
        fixed(width: 1200) {
          src
        }
      }
    }
    traces: file(relativePath: { eq: "api-monitor/traces-official.png" }) {
      ...ApiMonitorScreenshot
    }
    logs: file(relativePath: { eq: "api-monitor/logs-official.png" }) {
      ...ApiMonitorScreenshot
    }
    topology: file(relativePath: { eq: "api-monitor/topology-official.png" }) {
      ...ApiMonitorScreenshot
    }
    infra: file(relativePath: { eq: "api-monitor/infra-official.png" }) {
      ...ApiMonitorScreenshot
    }
    editorial: file(relativePath: { eq: "products/api-editorial-v1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
  fragment ApiMonitorScreenshot on File {
    childImageSharp {
      fluid(maxWidth: 1920, quality: 95) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }
`
